"use client"

import { forwardRef } from "react"
import type { ElementType, CSSProperties } from "react"
import { useSidebarContext } from "./hooks/useSidebarContext"
import { useSidebarIndicator } from "./hooks/useSidebarIndicator"
import type { SidebarIndicatorProps, SidebarIndicatorRenderProp } from "./Sidebar.types"
import { SIDEBAR_TAILWIND_CLASSES, SIDEBAR_DATA_ATTRIBUTES } from "./Sidebar.constants"

/**
 * Componente de indicador visual del Sidebar
 *
 * @description
 * Indicador visual que sigue al cursor del mouse (o item enfocado) dentro
 * del área de navegación. Proporciona feedback visual claro al usuario.
 *
 * Características:
 * - Se muestra solo cuando el mouse está sobre el área de items
 * - Sigue al cursor con animación suave
 * - Se posiciona en el item enfocado al navegar por teclado
 * - Totalmente customizable con className y style
 * - Soporta render props para control total del contenido
 * - Color y dimensiones configurables via contexto
 *
 * El indicador se oculta automáticamente cuando:
 * - El mouse sale del sidebar
 * - El cursor está fuera del área de items
 * - Se pierde el focus del sidebar
 *
 * @example
 * ```tsx
 * // Uso básico con estilos por defecto
 * <SidebarNav>
 *   <SidebarIndicator />
 *   <SidebarContent>...</SidebarContent>
 * </SidebarNav>
 * ```
 *
 * @example
 * ```tsx
 * // Con estilos custom
 * <SidebarIndicator className="bg-red-500 w-1" />
 * ```
 *
 * @example
 * ```tsx
 * // Con render props
 * <SidebarIndicator>
 *   {({ visible, position }) => (
 *     <div
 *       className={cn('indicator', visible && 'opacity-100')}
 *       style={{ top: position }}
 *     >
 *       <span>→</span>
 *     </div>
 *   )}
 * </SidebarIndicator>
 * ```
 *
 * @example
 * ```tsx
 * // Como elemento custom
 * <SidebarIndicator as={motion.div} animate={{ opacity: 1 }} />
 * ```
 */
export const SidebarIndicator = forwardRef<HTMLDivElement, SidebarIndicatorProps>(
  function SidebarIndicator({ children, className = "", style, as, ...props }, forwardedRef) {
    const { indicatorRef } = useSidebarContext()
    const { isVisible } = useSidebarIndicator()

    // Determinar si children es render prop
    const isRenderProp = typeof children === "function"

    // Estado del indicador
    const indicatorState = {
      visible: isVisible,
      position: 0, // La posición se gestiona via style.top en el ref
    }

    // Elemento a renderizar (default: div)
    const Component = (as || "div") as ElementType

    // Estilos del indicador (usando tokens CSS de shadcn en formato oklch)
    const indicatorStyles: CSSProperties = {
      background: "var(--sidebar-primary)",
      transform: "translateY(-50%)",
      opacity: isVisible ? 1 : 0,
      height: "var(--sidebar-indicator-height, 3rem)",
      width: "0.25rem",
      anchorName: "--sidebar-indicator",
      ...style,
    }

    // Combinar refs (forwarded + context)
    const setRefs = (node: HTMLDivElement | null) => {
      // Actualizar ref del contexto
      if (indicatorRef && "current" in indicatorRef) {
        ;(indicatorRef as React.MutableRefObject<HTMLDivElement | null>).current = node
      }

      // Actualizar ref forwarded
      if (typeof forwardedRef === "function") {
        forwardedRef(node)
      } else if (forwardedRef) {
        forwardedRef.current = node
      }
    }

    // Si hay render prop, delegar renderizado completo
    if (isRenderProp) {
      return (
        <Component ref={setRefs} {...props}>
          {(children as SidebarIndicatorRenderProp)(indicatorState)}
        </Component>
      )
    }

    // Renderizado por defecto
    return (
      <Component
        ref={setRefs}
        className={`sidebar-indicator ${SIDEBAR_TAILWIND_CLASSES.indicator} ${className}`}
        style={indicatorStyles}
        aria-hidden="true"
        {...{ [SIDEBAR_DATA_ATTRIBUTES.VISIBLE]: isVisible }}
        {...props}
      >
        {children}
      </Component>
    )
  },
)
